import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { ForbiddenApiErrorDialog } from '@/components/Error/ForbiddenApiErrorDialog';
import { GlobalErrorDialog } from '@/components/Error/GlobalErrorDialog';
import { ViewportLoader } from '@/components/Loading';
import { TooltipProvider } from '@/components/Tooltip/tooltip';
import { Toaster } from '@/components/ui/toaster';
import { GlobalAuthModal } from '@/features/auth/components/GlobalAuthModal';
import { initializeQRScannerWasm } from '@/features/qr-scanner/wasm-init';
import { EnvMode } from '@/lib/env/feature-flag';
import { initializeAllEventListeners } from '@/lib/events/event-listeners';
import type { LanguageProvider } from '@/lib/i18n/adapters/i18next-init';
import { initializeLogger } from '@/lib/logger';
import { queryClient } from '@/lib/queryClient';
import { router } from '@/lib/router';
import './index.css';

/**
 * MSWを起動する（開発環境のみ）
 * 環境変数 VITE_ENABLE_MSW=true で有効化
 */
async function enableMocking(): Promise<void> {
  // 本番環境では何もしない
  if (import.meta.env.PROD) {
    return;
  }

  // 環境変数でMSWが有効化されていない場合は何もしない。
  // 組織図（ADR-451 Phase 0）・組織改編の性能計測 fixture を使う場合も worker を起動する。
  if (import.meta.env.VITE_ENABLE_MSW !== 'true' && import.meta.env.VITE_ORG_CHART_PERF_FIXTURE !== 'true' && import.meta.env.VITE_ORG_PLAN_PERF_FIXTURE !== 'true') {
    return;
  }
  const {
    startMockWorker
  } = await import('@/mocks/browser');
  await startMockWorker();
  console.log('[MSW] Mock Service Worker started');
}

/**
 * デモ用 IndexedDB（Dexie.js）を初期化する（本番以外）
 * production ビルドでは動的 import ごと dead code elimination で除外される。
 * staging / demo 等の非 production モードでは Dexie を初期化し seed データを投入する。
 */
async function enableDemoDb(): Promise<void> {
  if (import.meta.env.MODE === EnvMode.production) {
    return;
  }
  const {
    initDemoDb
  } = await import('@/mocks/db');
  await initDemoDb();
  console.log('[DemoDB] Demo database initialized');
}
function initializeRootElement() {
  const rootElement = document.getElementById('root');
  if (!rootElement) {
    throw new Error('Root element not found');
  }
  if (!rootElement.innerHTML) {
    const root = createRoot(rootElement);
    return root;
  }
  return null;
}

// ルート要素を事前に初期化
const root = initializeRootElement();

/**
 * i18nの初期化を実行
 * 認証ストアから言語設定を取得してi18nを設定
 */
async function initializeI18n() {
  const {
    initializeI18n: initI18n
  } = await import('./lib/i18n/adapters/i18next-init');
  const {
    useAuthStore
  } = await import('./lib/auth');
  try {
    const authStore = useAuthStore.getState();
    let languageProvider: LanguageProvider | undefined;
    const authLanguageSettings: LanguageProvider = {
      sessionLocale: authStore.session?.locale,
      clientLocale: authStore.locale
    };
    if (!languageProvider) {
      languageProvider = authLanguageSettings;
    }

    // i18n初期化を実行
    await initI18n({}, languageProvider);
  } catch (error) {
    console.error('Failed to initialize i18n for app:', error);
    // エラーの場合もデフォルト設定でi18nを初期化
    await initI18n({});
  }
}
if (root) {
  root.render(<ViewportLoader />);
}
async function initializeApp() {
  try {
    initializeLogger();
    initializeAllEventListeners();

    // QRスキャナーのWASM設定（BarcodeDetectorが使用される前に実行）
    initializeQRScannerWasm();

    // i18nの初期化
    await initializeI18n();
    if (root) {
      root.render(<StrictMode>
          <Suspense fallback={<ViewportLoader />}>
            <QueryClientProvider client={queryClient}>
              {/*
                TooltipWrapper の per-instance Provider を廃止した単一 Provider。
                skipDelayDuration={0} は連続ホバー時の遅延スキップを無効化し、
                従来の per-instance Provider と同じ挙動（毎回 delayDuration を待つ）を保つため
               */}
              <TooltipProvider skipDelayDuration={0}>
                <RouterProvider router={router} />
                <Toaster />
                <GlobalErrorDialog />
                <ForbiddenApiErrorDialog />
                <GlobalAuthModal />
                {/* React Query Devtools - 開発環境でのみ表示 */}
                <ReactQueryDevtools initialIsOpen={false} position="bottom" buttonPosition="bottom-left" />
              </TooltipProvider>
            </QueryClientProvider>
          </Suspense>
        </StrictMode>);
    }
  } catch (error) {
    console.error('Failed to initialize application:', error);
    if (root) {
      root.render(<div className="flex h-screen w-screen items-center justify-center text-red-600">
          Failed to initialize application. Please reload the page.
        </div>);
    }
  }
}

// MSW とデモ DB の初期化を待ってからアプリを起動
// enableDemoDb は失敗してもアプリ起動を妨げない
Promise.all([enableMocking(), enableDemoDb().catch(console.error)]).then(() => initializeApp());